import React from 'react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '../../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import {
  Upload, X, CheckCircle, XCircle, RefreshCw, StickyNote, Search, Plus,
  Eye, ExternalLink, AlertTriangle, Activity, FileText, Trash2, Edit2,
  Mail, Send, Link2, ShieldAlert, Loader2,
} from 'lucide-react';

/* =========================================================
   CasoDetalleDialogs
   Renders ALL dialogs that used to be inline in CasoDetalle.
   Receives state & handlers from parent via props.
   ========================================================= */

export const CasoDetalleDialogs = ({
  caso, caseId, users,
  // Close dialog
  showCloseDialog, setShowCloseDialog, closeTemplates, closeReason, setCloseReason,
  closeTipoResolucion, setCloseTipoResolucion, tiposResolucion,
  pendingCloseState, handleConfirmClose,
  // Upload dialog
  showUploadDialog, setShowUploadDialog, uploadingFiles, dragOver,
  handleDragOver, handleDragLeave, handleDrop, handleFileSelect,
  removeFileFromUpload, uploadDocuments,
  // Note dialog
  showNoteDialog, setShowNoteDialog, newNote, setNewNote, handleAddNote,
  // Search/Link dialog
  showSearchDialog, setShowSearchDialog, searchTerm, setSearchTerm,
  searchResults, searching,
  handleAddCuenta, handleAddMovimiento, handleAddEmpresa, handleAddAlerta,
  // Doc preview
  showDocPreview, setShowDocPreview,
  // Alert action
  showAlertActionDialog, setShowAlertActionDialog,
  selectedAlertForAction, alertActionForm, setAlertActionForm,
  ALERT_STATES, handleAlertAction,
  // Vincular solicitud
  showVincularDialog, setShowVincularDialog,
  busquedaSolicitud, buscarSolicitudesParaVincular,
  buscandoSolicitudes, solicitudesBusqueda, vincularSolicitud,
  // Crear solicitud
  showSolicitudDialog, setShowSolicitudDialog,
  solicitudForm, setSolicitudForm, tiposSolicitud, gruposSolicitud,
  plantillasSolicitud, alertasDetalle, creandoSolicitud,
  handleTipoSolicitudChange, handlePlantillaSolicitudChange, handleCrearSolicitud,
  getCasoCvu, detectTipoBusqueda,
  // Email dialog
  showEmailDialog, setShowEmailDialog,
  emailForm, setEmailForm, enviandoEmail, setEnviandoEmail,
}) => {
  return (
    <>
      {/* Dialog: Cierre con Template de Resolución */}
      <Dialog open={showCloseDialog} onOpenChange={setShowCloseDialog}>
        <DialogContent className="max-w-lg" style={{ backgroundColor: '#ffffff' }}>
          <DialogHeader><DialogTitle>Cerrar Caso</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-sm font-medium text-slate-700 mb-1 block">Tipo de resolución</label>
              <select value={closeTipoResolucion || ''} onChange={e => setCloseTipoResolucion(e.target.value)}
                className="w-full border rounded-lg p-2.5 text-sm" data-testid="close-tipo-select">
                <option value="">Seleccionar...</option>
                {(tiposResolucion || []).map(t => (<option key={t} value={t}>{t}</option>))}
              </select>
            </div>
            {closeTemplates.length > 0 && (
              <div>
                <label className="text-sm font-medium text-slate-700 mb-2 block">Templates de resolución</label>
                <div className="grid gap-2">
                  {closeTemplates.map(t => (
                    <button key={t.id} onClick={() => setCloseReason(t.texto)}
                      className={`text-left p-2.5 rounded-lg border text-sm transition-all ${closeReason === t.texto ? 'border-blue-500 bg-blue-50 text-blue-800' : 'border-slate-200 hover:border-slate-300 hover:bg-slate-50 text-slate-700'}`}
                      data-testid={`template-${t.id}`}>
                      <span className="font-medium">{t.nombre}</span>
                      <p className="text-xs text-slate-500 mt-0.5">{t.texto}</p>
                    </button>
                  ))}
                </div>
              </div>
            )}
            <div>
              <label className="text-sm font-medium text-slate-700 mb-1 block">Observaciones de cierre</label>
              <textarea value={closeReason} onChange={e => setCloseReason(e.target.value)}
                placeholder="Describir la razón del cierre..." data-testid="close-reason-textarea"
                className="w-full border rounded-lg p-2.5 text-sm min-h-[80px] resize-none" />
            </div>
            <div className="flex gap-2 justify-end">
              <Button variant="outline" onClick={() => setShowCloseDialog(false)}>Cancelar</Button>
              <Button onClick={handleConfirmClose} data-testid="confirm-close-btn">
                Confirmar {pendingCloseState === 'BLOQUEO_PREVENTIVO' ? 'Bloqueo' : 'Cierre'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Dialog: Upload */}
      <Dialog open={showUploadDialog} onOpenChange={setShowUploadDialog}>
        <DialogContent className="max-w-2xl" style={{ backgroundColor: '#ffffff' }}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Upload className="text-purple-600" />Subir Documentos</DialogTitle>
          </DialogHeader>
          <div className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${dragOver ? 'border-purple-500 bg-purple-50' : 'border-slate-300'}`}
            onDragOver={handleDragOver} onDragLeave={handleDragLeave} onDrop={handleDrop}>
            <Upload size={40} className="mx-auto mb-2 text-slate-400" />
            <p className="text-slate-600">Arrastre archivos aquí o</p>
            <label className="mt-2 inline-block">
              <span className="text-purple-600 cursor-pointer hover:underline">seleccione archivos</span>
              <input type="file" multiple className="hidden" onChange={handleFileSelect} />
            </label>
          </div>
          {uploadingFiles.length > 0 && (
            <div className="mt-4 space-y-2 max-h-60 overflow-y-auto">
              {uploadingFiles.map(file => (
                <div key={file.id} className={`p-3 rounded-lg ${file.uploaded ? 'bg-green-50' : 'bg-slate-50'}`}>
                  <div className="flex items-center justify-between">
                    <span className="font-medium text-sm truncate max-w-xs">{file.nombre}</span>
                    <div className="flex items-center gap-2">
                      {file.uploading && <RefreshCw size={14} className="animate-spin text-purple-600" />}
                      {file.uploaded && <CheckCircle size={14} className="text-green-600" />}
                      {file.error && <XCircle size={14} className="text-red-600" />}
                      <button onClick={() => removeFileFromUpload(file.id)} className="text-slate-400 hover:text-red-600"><X size={14} /></button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowUploadDialog(false)}>Cancelar</Button>
            <button onClick={uploadDocuments} disabled={uploadingFiles.filter(f => !f.uploaded).length === 0}
              className="px-4 py-2 rounded-md text-white disabled:opacity-50" style={{ backgroundColor: '#7c3aed' }}>Subir</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog: Agregar Nota */}
      <Dialog open={showNoteDialog} onOpenChange={setShowNoteDialog}>
        <DialogContent style={{ backgroundColor: '#ffffff' }}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><StickyNote className="text-pink-500" />Nueva Nota</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Tipo de Nota</Label>
              <Select value={newNote.tipo} onValueChange={(v) => setNewNote({ ...newNote, tipo: v })}>
                <SelectTrigger style={{ backgroundColor: '#fff' }}><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="LLAMADA">Llamada</SelectItem>
                  <SelectItem value="EMAIL">Email</SelectItem>
                  <SelectItem value="REUNION">Reunión</SelectItem>
                  <SelectItem value="INVESTIGACION">Investigación</SelectItem>
                  <SelectItem value="OTRO">Otro</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Descripción</Label>
              <Textarea value={newNote.texto} onChange={(e) => setNewNote({ ...newNote, texto: e.target.value })}
                placeholder="Detalle de la nota..." rows={4} style={{ backgroundColor: '#fff' }} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNoteDialog(false)}>Cancelar</Button>
            <button onClick={handleAddNote} disabled={!newNote.texto.trim()}
              className="px-4 py-2 rounded-md text-white disabled:opacity-50" style={{ backgroundColor: '#7c3aed' }}>Agregar Nota</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog: Buscar y Vincular */}
      <Dialog open={!!showSearchDialog} onOpenChange={() => setShowSearchDialog(null)}>
        <DialogContent style={{ backgroundColor: '#ffffff' }}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Search className="text-purple-600" />
              Buscar {showSearchDialog === 'cuenta' ? 'Cuenta' : showSearchDialog === 'movimiento' ? 'Movimiento' : showSearchDialog === 'alerta' ? 'Alerta' : 'Empresa'}
            </DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <Input placeholder={`Buscar por ${showSearchDialog === 'cuenta' ? 'CUIT o nombre' : showSearchDialog === 'movimiento' ? 'ID o descripción' : showSearchDialog === 'alerta' ? 'título o descripción' : 'razón social o CUIT'}...`}
              value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} style={{ backgroundColor: '#fff' }} />
            <div className="mt-4 max-h-60 overflow-y-auto">
              {searching ? (
                <div className="text-center py-4"><RefreshCw className="animate-spin mx-auto" size={20} /></div>
              ) : searchResults.length === 0 ? (
                <p className="text-sm text-slate-400 text-center py-4">{searchTerm ? 'Sin resultados' : 'Ingrese un término de búsqueda'}</p>
              ) : (
                <div className="space-y-2">
                  {searchResults.map((item, idx) => (
                    <div key={idx} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg hover:bg-slate-100 cursor-pointer"
                      onClick={() => {
                        if (showSearchDialog === 'cuenta') handleAddCuenta(item.cuit);
                        else if (showSearchDialog === 'movimiento') handleAddMovimiento(item.id_coelsa || item.id);
                        else if (showSearchDialog === 'empresa') handleAddEmpresa(item.id);
                        else if (showSearchDialog === 'alerta') handleAddAlerta(item.id);
                        setShowSearchDialog(null);
                      }}>
                      <div>
                        <p className="font-medium text-sm">
                          {showSearchDialog === 'empresa' ? item.razon_social : showSearchDialog === 'alerta' ? item.titulo : item.razon_social || item.nombre || item.descripcion || 'Sin nombre'}
                        </p>
                        <p className="text-xs text-slate-500">
                          {showSearchDialog === 'cuenta' ? item.cuit : showSearchDialog === 'movimiento' ? `ID: ${item.id_coelsa || item.id}` : showSearchDialog === 'alerta' ? `${item.severidad} - ${new Date(item.fecha_creacion || item.created_at).toLocaleDateString()}` : item.cuit}
                        </p>
                      </div>
                      <Plus size={16} className="text-purple-600" />
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setShowSearchDialog(null)}>Cerrar</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog: Preview Documento */}
      <Dialog open={!!showDocPreview} onOpenChange={() => setShowDocPreview(null)}>
        <DialogContent style={{ backgroundColor: '#ffffff' }}>
          <DialogHeader><DialogTitle>{showDocPreview?.nombre}</DialogTitle></DialogHeader>
          {showDocPreview && (
            <div className="py-4">
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div><Label className="text-xs text-slate-500">Tipo</Label><p className="text-sm">{showDocPreview.tipo}</p></div>
                <div><Label className="text-xs text-slate-500">Subido por</Label><p className="text-sm">{showDocPreview.subido_por}</p></div>
                <div><Label className="text-xs text-slate-500">Fecha</Label><p className="text-sm">{new Date(showDocPreview.fecha).toLocaleString()}</p></div>
              </div>
              <div className="flex justify-center">
                <a href={showDocPreview.url} target="_blank" rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-md text-white" style={{ backgroundColor: '#7c3aed' }}>
                  <ExternalLink size={16} />Abrir Documento
                </a>
              </div>
            </div>
          )}
          <DialogFooter><Button variant="outline" onClick={() => setShowDocPreview(null)}>Cerrar</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog: Acciones sobre Alerta */}
      <Dialog open={showAlertActionDialog} onOpenChange={setShowAlertActionDialog}>
        <DialogContent style={{ backgroundColor: '#ffffff' }} className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><AlertTriangle className="text-amber-500" />Resolver Alerta</DialogTitle>
            <DialogDescription>{selectedAlertForAction?.titulo}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-2">
              <Button variant="outline" className={`h-14 flex flex-col gap-1 ${alertActionForm.estado === 'FALSO_POSITIVO' ? 'ring-2 ring-green-500 bg-green-50' : ''}`}
                onClick={() => setAlertActionForm({ ...alertActionForm, estado: 'FALSO_POSITIVO' })}>
                <CheckCircle size={20} className="text-green-600" /><span className="text-xs">Lícita / Falso Positivo</span>
              </Button>
              <Button variant="outline" className={`h-14 flex flex-col gap-1 ${alertActionForm.estado === 'ESCALADA' ? 'ring-2 ring-red-500 bg-red-50' : ''}`}
                onClick={() => setAlertActionForm({ ...alertActionForm, estado: 'ESCALADA' })}>
                <AlertTriangle size={20} className="text-red-600" /><span className="text-xs">Escalar / Sospechosa</span>
              </Button>
            </div>
            <div>
              <Label>Cambiar Estado</Label>
              <Select value={alertActionForm.estado} onValueChange={(v) => setAlertActionForm({ ...alertActionForm, estado: v })}>
                <SelectTrigger style={{ backgroundColor: '#fff' }}><SelectValue placeholder="Seleccionar estado" /></SelectTrigger>
                <SelectContent>{ALERT_STATES.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Comentario / Justificación *</Label>
              <Textarea value={alertActionForm.comentario} onChange={(e) => setAlertActionForm({ ...alertActionForm, comentario: e.target.value })}
                placeholder="Explique el motivo de esta resolución..." rows={3} style={{ backgroundColor: '#fff' }} />
              <p className="text-xs text-slate-500 mt-1">El comentario se guardará en el historial de la alerta</p>
            </div>
            {selectedAlertForAction && (
              <div className="p-3 bg-amber-50 rounded-lg text-sm">
                <div className="flex items-center gap-2 mb-2">
                  <Badge className={`${selectedAlertForAction?.severidad === 'CRITICA' ? 'bg-red-100 text-red-700' : selectedAlertForAction?.severidad === 'ALTA' ? 'bg-orange-100 text-orange-700' : selectedAlertForAction?.severidad === 'MEDIA' ? 'bg-yellow-100 text-yellow-700' : 'bg-green-100 text-green-700'}`}>
                    {selectedAlertForAction?.severidad}
                  </Badge>
                  <span className="text-slate-500">Estado actual: {selectedAlertForAction?.estado?.replace('_', ' ') || 'NUEVA'}</span>
                </div>
                <p className="text-slate-600 line-clamp-2">{selectedAlertForAction?.descripcion}</p>
                {selectedAlertForAction?.movimientos_ids?.length > 0 && (
                  <p className="text-xs text-amber-700 mt-2"><Activity size={12} className="inline mr-1" />{selectedAlertForAction.movimientos_ids.length} transacciones asociadas</p>
                )}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAlertActionDialog(false)}>Cancelar</Button>
            <button onClick={handleAlertAction} disabled={!alertActionForm.comentario.trim()}
              className="px-4 py-2 rounded-md text-white disabled:opacity-50" style={{ backgroundColor: '#f59e0b' }}>Guardar Resolución</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog: Vincular Solicitud Existente */}
      <Dialog open={showVincularDialog} onOpenChange={(open) => { setShowVincularDialog(open); if (!open) { buscarSolicitudesParaVincular(''); } }}>
        <DialogContent className="bg-white max-w-lg">
          <DialogHeader>
            <DialogTitle>Vincular Solicitud Existente</DialogTitle>
            <DialogDescription>Busque una solicitud por referencia, descripción o entidad para vincularla a este caso.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="relative">
              <Input placeholder="Buscar solicitud por referencia, entidad, descripción..."
                value={busquedaSolicitud} onChange={(e) => buscarSolicitudesParaVincular(e.target.value)} data-testid="buscar-solicitud-input" />
              {buscandoSolicitudes && <div className="absolute right-3 top-3"><div className="animate-spin h-4 w-4 border-2 border-blue-600 border-t-transparent rounded-full" /></div>}
            </div>
            {solicitudesBusqueda.length > 0 ? (
              <div className="max-h-[300px] overflow-y-auto space-y-2">
                {solicitudesBusqueda.map(sol => (
                  <div key={sol.id} className="border rounded-lg p-3 hover:bg-slate-50 flex items-center justify-between">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-mono text-sm font-medium">{sol.numero_referencia}</span>
                        <span className={`text-xs px-2 py-0.5 rounded-full ${sol.estado === 'PENDIENTE' ? 'bg-yellow-100 text-yellow-700' : sol.estado === 'EN_PROCESO' ? 'bg-blue-100 text-blue-700' : 'bg-slate-100 text-slate-700'}`}>{sol.estado}</span>
                      </div>
                      <p className="text-sm text-slate-600 truncate">{sol.tipo_solicitud} - {sol.entidad_nombre || sol.descripcion?.substring(0, 50)}</p>
                    </div>
                    <Button size="sm" onClick={() => vincularSolicitud(sol.id)} className="bg-purple-600 hover:bg-purple-700 text-white ml-3 shrink-0" data-testid={`vincular-${sol.id}`}>
                      <Link2 size={14} className="mr-1" />Vincular
                    </Button>
                  </div>
                ))}
              </div>
            ) : busquedaSolicitud.length >= 2 && !buscandoSolicitudes ? (
              <p className="text-sm text-slate-500 text-center py-4">No se encontraron solicitudes</p>
            ) : (
              <p className="text-sm text-slate-400 text-center py-4">Escriba al menos 2 caracteres para buscar</p>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Dialog: Crear Solicitud */}
      <Dialog open={showSolicitudDialog} onOpenChange={setShowSolicitudDialog}>
        <DialogContent className="bg-white" style={{ maxWidth: '700px' }}>
          <DialogHeader>
            <DialogTitle>Nueva Solicitud desde Caso</DialogTitle>
            <DialogDescription>Crear una solicitud vinculada a este caso. Al completarse, se puede cerrar el caso automáticamente.</DialogDescription>
          </DialogHeader>
          <div className="flex justify-start pt-1">
            <Button onClick={handleCrearSolicitud}
              disabled={creandoSolicitud || !solicitudForm.tipo_solicitud || !solicitudForm.descripcion || (solicitudForm.enviar_email && !solicitudForm.email_destinatario && !solicitudForm.buscar_email_waya)}
              className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="crear-solicitud-top-btn">
              {creandoSolicitud ? <><Loader2 size={16} className="mr-2 animate-spin" />Creando...</> : <><Plus size={16} className="mr-2" />Crear Solicitud</>}
            </Button>
          </div>
          <div className="space-y-4 py-4">
            <div>
              <Label className="text-sm font-medium">Tipo de Solicitud *</Label>
              <Select value={solicitudForm.tipo_solicitud} onValueChange={handleTipoSolicitudChange}>
                <SelectTrigger className="mt-1"><SelectValue placeholder="Seleccione el tipo..." /></SelectTrigger>
                <SelectContent>
                  {tiposSolicitud.map(tipo => (
                    <SelectItem key={tipo.id} value={tipo.id}><div><p className="font-medium">{tipo.nombre}</p><p className="text-xs text-slate-500">{tipo.descripcion}</p></div></SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-sm font-medium">Descripción / Motivo *</Label>
              <Textarea value={solicitudForm.descripcion} onChange={(e) => setSolicitudForm({ ...solicitudForm, descripcion: e.target.value })}
                placeholder="Describa el motivo de la solicitud..." rows={3} className="mt-1" />
            </div>
            {/* Selección de alertas del caso */}
            {((caso?.alertas && caso.alertas.length > 0) || (alertasDetalle && alertasDetalle.length > 0)) && (
              <div className="space-y-2">
                <Label className="text-sm font-medium">Adjuntar alertas del caso</Label>
                <div className="border border-slate-200 rounded-lg max-h-48 overflow-y-auto">
                  {(caso?.alertas || alertasDetalle || []).map(alerta => {
                    const alertaId = alerta.id || alerta.alerta_id;
                    const isSelected = solicitudForm.alertas_seleccionadas.includes(alertaId);
                    const titulo = alerta.titulo || alerta.title || alerta.tipo_alerta || `Alerta ${alertaId?.slice(0,8)}`;
                    const severidad = alerta.severidad || alerta.severity || '';
                    const sevColor = { ALTA: 'text-red-600', CRITICA: 'text-red-700', MEDIA: 'text-amber-600', BAJA: 'text-green-600' }[severidad?.toUpperCase()] || 'text-slate-500';
                    return (
                      <label key={alertaId} className={`flex items-start gap-3 px-3 py-2 cursor-pointer hover:bg-slate-50 border-b border-slate-100 last:border-b-0 ${isSelected ? 'bg-blue-50' : ''}`}>
                        <input type="checkbox" checked={isSelected}
                          onChange={() => { const sel = solicitudForm.alertas_seleccionadas; const next = isSelected ? sel.filter(id => id !== alertaId) : [...sel, alertaId]; setSolicitudForm({ ...solicitudForm, alertas_seleccionadas: next }); }}
                          className="mt-1 rounded border-slate-300" />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-slate-800 truncate">{titulo}</p>
                          <p className="text-xs text-slate-500 truncate">{alerta.descripcion || alerta.description || ''}</p>
                          <div className="flex gap-3 mt-1 text-xs">
                            {severidad && <span className={`font-medium ${sevColor}`}>{severidad}</span>}
                            {alerta.estado && <span className="text-slate-400">{alerta.estado}</span>}
                          </div>
                        </div>
                      </label>
                    );
                  })}
                </div>
                {solicitudForm.alertas_seleccionadas.length > 0 && <p className="text-xs text-blue-600 font-medium">{solicitudForm.alertas_seleccionadas.length} alerta(s) seleccionada(s)</p>}
              </div>
            )}
            <div>
              <Label className="text-sm font-medium">Prioridad</Label>
              <Select value={solicitudForm.prioridad} onValueChange={(v) => setSolicitudForm({ ...solicitudForm, prioridad: v })}>
                <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="CRITICA">Crítica</SelectItem><SelectItem value="ALTA">Alta</SelectItem>
                  <SelectItem value="MEDIA">Media</SelectItem><SelectItem value="BAJA">Baja</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {/* Asignación */}
            <div className="space-y-3 p-3 bg-slate-50 rounded-lg border border-slate-200">
              <Label className="text-sm font-medium">Asignar a</Label>
              <div className="flex gap-4">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="radio" name="asignar_a" value="grupo" checked={solicitudForm.asignar_a === 'grupo'}
                    onChange={() => setSolicitudForm({ ...solicitudForm, asignar_a: 'grupo', responsable_id: '' })} className="text-blue-600" />
                  <span className="text-sm">Grupo</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="radio" name="asignar_a" value="persona" checked={solicitudForm.asignar_a === 'persona'}
                    onChange={() => setSolicitudForm({ ...solicitudForm, asignar_a: 'persona', grupo_id: '' })} className="text-blue-600" />
                  <span className="text-sm">Persona específica</span>
                </label>
              </div>
              {solicitudForm.asignar_a === 'grupo' && (
                <Select value={solicitudForm.grupo_id} onValueChange={(v) => setSolicitudForm({ ...solicitudForm, grupo_id: v })}>
                  <SelectTrigger><SelectValue placeholder="Seleccionar grupo..." /></SelectTrigger>
                  <SelectContent>{gruposSolicitud.map(g => <SelectItem key={g.id} value={g.id}>{g.nombre}</SelectItem>)}</SelectContent>
                </Select>
              )}
              {solicitudForm.asignar_a === 'persona' && (
                <Select value={solicitudForm.responsable_id} onValueChange={(v) => setSolicitudForm({ ...solicitudForm, responsable_id: v })}>
                  <SelectTrigger><SelectValue placeholder="Seleccionar persona..." /></SelectTrigger>
                  <SelectContent>{users.map(u => <SelectItem key={u.id} value={u.id}>{u.nombre} ({u.email})</SelectItem>)}</SelectContent>
                </Select>
              )}
            </div>
            <div className="flex items-center gap-2 p-3 bg-blue-50 rounded-lg">
              <input type="checkbox" id="cerrar_caso" checked={solicitudForm.cerrar_caso_al_completar}
                onChange={(e) => setSolicitudForm({ ...solicitudForm, cerrar_caso_al_completar: e.target.checked })} className="rounded border-slate-300" />
              <label htmlFor="cerrar_caso" className="text-sm text-slate-700">Cerrar el caso automáticamente al completar esta solicitud</label>
            </div>
            {solicitudForm.tipo_solicitud === 'BLOQUEO_CUENTA' && getCasoCvu() && (
              <div className="space-y-2 p-3 bg-red-50 rounded-lg border border-red-200" data-testid="bloqueo-waya-section">
                <div className="flex items-center gap-2">
                  <input type="checkbox" id="bloquear_cuenta_waya" checked={solicitudForm.bloquear_cuenta}
                    onChange={(e) => setSolicitudForm({ ...solicitudForm, bloquear_cuenta: e.target.checked })} className="rounded border-red-300" />
                  <Label htmlFor="bloquear_cuenta_waya" className="text-sm font-medium text-red-700 flex items-center gap-1 cursor-pointer">
                    <ShieldAlert size={14} />Bloquear cuenta automáticamente en portal Waya
                  </Label>
                </div>
                {solicitudForm.bloquear_cuenta && (
                  <p className="text-xs text-red-600 ml-6">Se bloqueará la cuenta {detectTipoBusqueda(getCasoCvu()) === 'cvu' ? 'CVU' : 'CUIT'}: <span className="font-mono font-medium">{getCasoCvu()}</span></p>
                )}
              </div>
            )}
            {/* Sección de Email */}
            <div className="space-y-3 p-3 bg-slate-50 rounded-lg border border-slate-200" data-testid="email-solicitud-section">
              <div className="flex items-center gap-2">
                <input type="checkbox" id="enviar_email_solicitud" checked={solicitudForm.enviar_email}
                  onChange={(e) => setSolicitudForm({ ...solicitudForm, enviar_email: e.target.checked })} className="rounded border-slate-300" />
                <Label htmlFor="enviar_email_solicitud" className="font-medium flex items-center gap-1 cursor-pointer"><Mail size={14} />Enviar email de notificación</Label>
              </div>
              {solicitudForm.enviar_email && (
                <div className="space-y-3 pt-2 border-t border-slate-200">
                  <div className="flex items-center gap-2">
                    <input type="checkbox" id="buscar_email_waya" checked={solicitudForm.buscar_email_waya || false}
                      onChange={(e) => setSolicitudForm({ ...solicitudForm, buscar_email_waya: e.target.checked, email_destinatario: e.target.checked ? '' : solicitudForm.email_destinatario })} className="rounded border-slate-300" />
                    <Label htmlFor="buscar_email_waya" className="text-sm text-slate-600 cursor-pointer">Buscar email automáticamente en Waya</Label>
                  </div>
                  {!solicitudForm.buscar_email_waya && (
                    <div><Label className="text-sm">Destinatario *</Label>
                      <Input type="email" value={solicitudForm.email_destinatario} onChange={(e) => setSolicitudForm({ ...solicitudForm, email_destinatario: e.target.value })}
                        placeholder="email@ejemplo.com" className="mt-1" data-testid="email-destinatario-input" /></div>
                  )}
                  {solicitudForm.buscar_email_waya && <p className="text-xs text-amber-600 bg-amber-50 rounded p-2">El sistema buscará el email del cliente en el portal Waya.</p>}
                  {plantillasSolicitud.length > 0 && (
                    <div><Label className="text-sm">Plantilla (opcional)</Label>
                      <Select value={solicitudForm.plantilla_id} onValueChange={handlePlantillaSolicitudChange}>
                        <SelectTrigger className="mt-1"><SelectValue placeholder="Seleccionar plantilla..." /></SelectTrigger>
                        <SelectContent>{plantillasSolicitud.filter(p => p.activo).map(p => <SelectItem key={p.id} value={p.id}>{p.nombre}</SelectItem>)}</SelectContent>
                      </Select></div>
                  )}
                  <div><Label className="text-sm">Asunto</Label>
                    <Input value={solicitudForm.email_asunto} onChange={(e) => setSolicitudForm({ ...solicitudForm, email_asunto: e.target.value })}
                      placeholder="Asunto del email..." className="mt-1" data-testid="email-asunto-input" /></div>
                  <div><Label className="text-sm">Contenido</Label>
                    <Textarea value={solicitudForm.email_contenido} onChange={(e) => setSolicitudForm({ ...solicitudForm, email_contenido: e.target.value })}
                      placeholder="Contenido del email..." rows={4} className="mt-1" data-testid="email-contenido-input" /></div>
                </div>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSolicitudDialog(false)}>Cancelar</Button>
            <Button onClick={handleCrearSolicitud}
              disabled={creandoSolicitud || !solicitudForm.tipo_solicitud || !solicitudForm.descripcion || (solicitudForm.enviar_email && !solicitudForm.email_destinatario && !solicitudForm.buscar_email_waya)}
              className="bg-blue-600 hover:bg-blue-700 text-white">
              {creandoSolicitud ? <><Loader2 size={16} className="mr-2 animate-spin" />Creando...</> : <><Plus size={16} className="mr-2" />Crear Solicitud</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog Enviar Email */}
      <Dialog open={showEmailDialog} onOpenChange={setShowEmailDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Mail className="text-blue-500" size={20} />Enviar Resumen por Email</DialogTitle>
            <DialogDescription>Se enviará un resumen del caso al destinatario indicado.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div><Label>Email Destinatario *</Label>
              <Input type="email" value={emailForm.email} onChange={(e) => setEmailForm({ ...emailForm, email: e.target.value })} placeholder="destinatario@ejemplo.com" className="mt-1" /></div>
            <div><Label>Asunto</Label>
              <Input value={emailForm.asunto} onChange={(e) => setEmailForm({ ...emailForm, asunto: e.target.value })} placeholder="Asunto del email" className="mt-1" /></div>
            <div><Label>Mensaje (opcional)</Label>
              <Textarea value={emailForm.mensaje} onChange={(e) => setEmailForm({ ...emailForm, mensaje: e.target.value })} placeholder="Mensaje adicional..." className="mt-1" rows={3} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEmailDialog(false)}>Cancelar</Button>
            <Button onClick={async () => {
              if (!emailForm.email) { return; }
              setEnviandoEmail(true);
              try { await (await import('../../utils/axios')).default.post(`/informes/caso/${caso.id}/enviar-email`, emailForm);
                setShowEmailDialog(false);
              } catch {} finally { setEnviandoEmail(false); }
            }} disabled={enviandoEmail || !emailForm.email} className="bg-blue-600 hover:bg-blue-700 text-white">
              {enviandoEmail ? <><Loader2 size={16} className="mr-2 animate-spin" />Enviando...</> : <><Send size={16} className="mr-2" />Enviar</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
